import React from 'react'

import { useLightMode } from '@/hooks/useLightMode'
import { usePermissions } from '@/hooks/usePermissions'
import { useTranslation } from 'react-i18next'

import {
  Box,
  Flex,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
  Text,
} from '@chakra-ui/react'

import { Modal } from '@/components/overlays'
import { EditPencilButton, OverflowText } from '@/components/ui'
import { EditDeviceFieldsI } from '@/features/device-crud'
import { DeviceType } from '@/graphql/generated/schemas'

import { DeviceConfigurationT } from '../types/types'

interface DevicesMisconfiguredModalIProps {
  isOpen: boolean
  devices: DeviceConfigurationT[]
  onClose: () => void
  onEditDevice: (device: EditDeviceFieldsI) => void
}

export const DevicesMisconfiguredModal = ({
  isOpen,
  devices,
  onClose,
  onEditDevice,
}: DevicesMisconfiguredModalIProps) => {
  const { t } = useTranslation()
  const mode = useLightMode()
  const { shouldEnableUpdateDevice } = usePermissions()

  return (
    <Modal
      isCentered
      isOpen={isOpen}
      motionPreset='slideInBottom'
      onClose={onClose}
      scrollBehavior='inside'
      size='xl'
    >
      <ModalOverlay />
      <ModalContent
        bg={`bg.card-elevated.${mode}`}
        color={`text.primary.${mode}`}
        py='6'
        rounded='20px'
      >
        <ModalCloseButton color={`text.primary.${mode}`} opacity={0.4} />

        <Box fontSize='20px' fontWeight={500} px='24px'>
          {t('automations:devices_misconfigured_modal_title')}
        </Box>

        <ModalBody p='24px'>
          <Flex flexDir='column' gap='5px'>
            {devices.map((d) => (
              <Flex
                alignItems='center'
                border='1px solid'
                borderColor={`border.subtle.${mode}`}
                borderRadius='10px'
                justifyContent='space-between'
                key={d.id}
                px='16px'
                py='8px'
              >
                <OverflowText fontSize='14px' maxW='60%' noOfLines={1}>
                  {d.name}
                </OverflowText>
                <Flex alignItems='center' gap='8px'>
                  <DeviceStatus isAssociated={d.isAssociated} />
                  <EditPencilButton
                    disabledTooltipLabel={t(
                      'automations:device_edit_disabled_tooltip'
                    )}
                    isDisabled={!shouldEnableUpdateDevice}
                    isLoading={false}
                    onClick={() =>
                      onEditDevice({
                        id: d.id,
                        name: d.name,
                        floorId: d.floorId,
                        type: DeviceType.Door,
                      })
                    }
                    tooltipLabel={t('automations:device_edit_tooltip')}
                  />
                </Flex>
              </Flex>
            ))}
          </Flex>
        </ModalBody>
      </ModalContent>
    </Modal>
  )
}

export const DeviceStatus = ({ isAssociated }: { isAssociated: boolean }) => {
  const { t } = useTranslation()
  const mode = useLightMode()
  const deviceText = isAssociated
    ? t('automations:device_status_missing_camera_grid')
    : t('automations:device_status_missing_device_association')

  return (
    <Box
      bg={`bg.disabled.${mode}`}
      border='1px solid'
      borderColor={`bg.disabled.${mode}`}
      borderRadius='12px'
      color={`text.secondary.${mode}`}
      fontSize='12px'
      fontWeight={500}
      p='8px'
    >
      <Text noOfLines={1}>{deviceText}</Text>
    </Box>
  )
}
